'use client';

/* Built In Imports */
import React, { useState } from 'react';
import { useRouter } from 'next/router';

/* External Imports */
import { Box, Button, Flex, Input, Select, Text } from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

/* Internal Imports */
import { downloadUrl } from '@msr/components/Utility/utils';
import { mixPanel } from '@utils/Mixpanel';

const validationSchema = Yup.object().shape({
  city: Yup.string()
    .matches(/^[A-Za-z\s]+$/, 'City name must be a alphabets')
    .required('City is required.'),

  no_of_guests: Yup.string()
    .matches(/^\d+$/, 'No of guests must be a number')
    .required('No of guests is required.'),

  isha_program_flag: Yup.string().required('Isha program flag is required.'),
});

const commonInputStyles = {
  fontFamily: "'FedraSansStd-book', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  border: '1px solid #aea493',
  borderBottom: '1px solid #a1a1a1',
  borderRadius: '6px',
  height: '56px',
  backgroundColor: '#fff',
  padding: '10px',
  color: '#605D5D',
  _placeholder: { color: '#605D5D', fontSize: '16px' },
};

const commonButtonStyles = {
  className: 'orange-button',
  display: 'block',
  fontWeight: '500',
  fontFamily: 'FedraSansStd-medium',
  fontSize: '16px',
  bgColor: 'yellow.100',
  h: 'auto',
  minH: '50px',
  border: 'none',
  textAlign: 'center',
  padding: '16px 56px',
  borderRadius: '5px',
  color: 'black',
  textDecoration: 'none',
  width: 'auto',
  _hover: {
    bg: 'fulvous',
  },
};

export const Downloadform = ({ downloadData, isMobile, handleClose }) => {
  const router = useRouter();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const Mixpanel = mixPanel(router.asPath);

  const handleSubmit = (values, { resetForm }) => {
    const { city, no_of_guests, isha_program_flag } = values;

    // Mixpanel Trigger
    Mixpanel.track('HWP_Download_resources', {
      cityName: city,
      noOfGuests: no_of_guests,
      ishaProgramFlag: isha_program_flag,
    });

    // Download file
    downloadUrl(
      downloadData?.data?.pdfLink,
      `HostGuide-${new Date().getMilliseconds()}`
    );
    setFormSubmitted(true);
    resetForm();
  };

  const handleContinueClick = () => {
    Mixpanel.track('HWP_Continue_to_invitation');
    window.open(downloadData?.data?.continueLink, '_blank');
    if (isMobile) {
      handleClose();
    }
    setFormSubmitted(false);
  };

  return (
    <Box pt={{ base: '15px', md: '45px' }} textAlign="center">
      {!formSubmitted && (
        <Text
          fontFamily={'FedraSansStd-A-medium'}
          fontWeight={500}
          fontSize={{ base: '20px', sm: '24px', md: '30px' }}
          lineHeight={{ base: '33.4px', md: '50.1px' }}
          pb={{ base: '20px', md: '11px' }}
        >
          {downloadData?.data?.title}
        </Text>
      )}

      {formSubmitted ? (
        <Flex
          justifyContent="center"
          direction="column"
          alignItems="center"
          textAlign="center"
        >
          <Box m="9px" pb={{ base: '15px', md: '32px' }}>
            <svg
              width="56"
              height="56"
              viewBox="0 0 56 56"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M36.4336 20.5L23.1211 37L17.2044 31M54.1836 28C54.1836 42.9117 42.2632 55 27.5586 55C12.854 55 0.933594 42.9117 0.933594 28C0.933594 13.0883 12.854 1 27.5586 1C42.2632 1 54.1836 13.0883 54.1836 28Z"
                stroke="#FFA735"
                strokeWidth="1.75"
                strokeLinecap="square"
              />
            </svg>
          </Box>
          <Text
            fontFamily="FedraSansStd-A-medium"
            fontSize={{ base: '20px', md: '30px' }}
            lineHeight={{ md: '36px' }}
            px={{ base: '2rem', md: '8rem' }}
            fontWeight={500}
            pb="32px"
            mb={{ base: '2rem' }}
          >
            {downloadData?.data?.guideDownloaded}
          </Text>
          <Text
            fontFamily="FedraSansStd-medium"
            fontSize={{ base: '20px', md: '22px' }}
            fontWeight={500}
            pb="38px"
            maxW="452px"
          >
            {downloadData?.data?.clickContinue}
          </Text>
          <Box display="grid" placeContent="center">
            <Button
              {...commonButtonStyles}
              onClick={handleContinueClick}
              width={{ md: '212px' }}
              height={{ md: '56px' }}
            >
              {downloadData?.data?.continue}
            </Button>
          </Box>
        </Flex>
      ) : (
        <Formik
          initialValues={{ city: '', no_of_guests: '', isha_program_flag: '' }}
          validationSchema={validationSchema}
          validate={values => {
            let errors = {};

            // Check if all fields are empty first
            const hasError =
              !values.city && !values.no_of_guests && !values.isha_program_flag;

            if (hasError) errors.all = 'All fields are Mandatory.';
            return errors;
          }}
          onSubmit={handleSubmit}
        >
          {({ errors }) => {
            // Get the first error message
            let firstError =
              errors.all || Object.values(errors).find(error => error);

            return (
              <Form>
                <Flex flexDirection="column">
                  <Field name="city">
                    {({ field }) => (
                      <Input
                        {...field}
                        placeholder={downloadData?.data?.city}
                        {...commonInputStyles}
                        mb="20px"
                      />
                    )}
                  </Field>

                  <Field name="no_of_guests">
                    {({ field }) => (
                      <Input
                        {...field}
                        placeholder={downloadData?.data?.guests}
                        {...commonInputStyles}
                        mb="20px"
                      />
                    )}
                  </Field>

                  <Field
                    as={Select}
                    name="isha_program_flag"
                    {...commonInputStyles}
                    p="0"
                  >
                    <option value="" disabled>
                      {downloadData?.data?.attended}
                    </option>
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </Field>

                  {/* Error message */}
                  {firstError && (
                    <Text
                      mt="7px"
                      fontFamily="Open Sans"
                      fontSize="16px"
                      fontWeight={600}
                      color="#FF0101"
                      textAlign="left"
                      ps="18px"
                    >
                      {firstError}
                    </Text>
                  )}

                  <Box
                    display="grid"
                    placeContent="center"
                    pt={firstError ? '18px' : '40px'}
                  >
                    <Button
                      type="submit"
                      {...commonButtonStyles}
                      width={{ md: '212px' }}
                      height={{ md: '56px' }}
                    >
                      {downloadData?.data?.btnText}
                    </Button>
                  </Box>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      )}
    </Box>
  );
};
